import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { WS_COLOR } from './tokens';

export interface SectionProps {
  title: string;
  description?: ReactNode;
  /** Buttons at the right of the title row. */
  actions?: ReactNode;
  children: ReactNode;
  id?: string;
}

/**
 * A flat section inside a record or a form: a title, an optional line of help, and content,
 * separated from the next section by a rule. Never a card inside a card (DESIGN.md Shapes).
 */
export function Section({ title, description, actions, children, id }: SectionProps) {
  const titleId = id ? `${id}-title` : undefined;
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={titleId}
      sx={{
        py: 5,
        borderTop: `1px solid ${WS_COLOR.border}`,
        '&:first-of-type': { borderTop: 0, pt: 0 },
        scrollMarginTop: 8,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography id={titleId} variant="h2" component="h2">
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ color: WS_COLOR.textMuted, mt: 0.5 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {actions ? <Box sx={{ display: 'flex', gap: 2, flex: 'none' }}>{actions}</Box> : null}
      </Box>
      {children}
    </Box>
  );
}
